import React, { useEffect, useRef, useState } from 'react';
import { Box, Button as MuiButton, Container, MenuItem, TextField } from '@mui/material';
import Text from '../../components/UI/atoms/Text';
import Card from '../../components/UI/molecules/Card';
import Button from '../../components/UI/atoms/Button';
import FirmTable from './components/FirmTable';
import AdminNav from './components/AdminNav';
import CreateAccountModal from './components/CreateAccountModal';
import { authGet } from '../../services/authCalls';
import { useApp } from '../../AppContext';

export default function Firms() {
  const { handleMessage } = useApp();
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [firms, setFirms] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState({ firmName: '', userName: '', email: '', subscriptionTag: '', renewalFilter: '' });
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const debounceRef = useRef(null);

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  const load = (page, searchParams, size) => {
    const p = page ?? pageNo;
    const s = searchParams ?? search;
    const ps = size ?? pageSize;
    setError(null);
    const params = new URLSearchParams({ pageNo: p, pageSize: ps });
    if (s.firmName.trim()) params.set('firmName', s.firmName.trim());
    if (s.userName.trim()) params.set('userName', s.userName.trim());
    if (s.email.trim()) params.set('email', s.email.trim());
    if (s.subscriptionTag) params.set('subscriptionTag', s.subscriptionTag);
    if (s.renewalFilter) params.set('renewalFilter', s.renewalFilter);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);
    authGet(`/api/admin/firms?${params.toString()}`)
      .then(res => { setFirms(res?.data || []); })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || `Error ${err?.response?.status || ''} loading firms`;
        setError(msg);
        handleMessage('error', msg);
      });
  };

  useEffect(() => {
    load(pageNo, search, pageSize);
    // eslint-disable-next-line
  }, [pageNo, sortBy, sortDir]);

  const handleSearchChange = (field, value) => {
    const next = { ...search, [field]: value };
    setSearch(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPageNo(1);
      load(1, next, pageSize);
    }, 400);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPageNo(1);
    load(1, search, newSize);
  };

  const clearSearch = () => {
    const empty = { firmName: '', userName: '', email: '', subscriptionTag: '', renewalFilter: '' };
    setSearch(empty);
    setSortBy(null);
    setSortDir(null);
    setPageNo(1);
    load(1, empty, pageSize);
  };

  const handlePage = (delta) => {
    const next = Math.max(1, pageNo + delta);
    setPageNo(next);
  };

  return (
    <Container maxWidth={false} sx={{ py: '32px', px: { xs: '16px', md: '40px' } }}>
      <AdminNav />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px' }}>
        <Text type='h1'>Firms</Text>
        <MuiButton variant='contained' size='small' onClick={() => setCreateOpen(true)}>+ Add New Account</MuiButton>
      </Box>

      <Card sx={{ margin: 0 }}>
        <Box sx={{ display: 'flex', gap: '12px', mb: '20px', flexWrap: 'wrap' }}>
          <TextField
            size='small'
            placeholder='Search firm name…'
            value={search.firmName}
            onChange={e => handleSearchChange('firmName', e.target.value)}
            sx={{ width: '220px' }}
          />
          <TextField
            size='small'
            placeholder='Search user name…'
            value={search.userName}
            onChange={e => handleSearchChange('userName', e.target.value)}
            sx={{ width: '220px' }}
          />
          <TextField
            size='small'
            placeholder='Search email…'
            value={search.email}
            onChange={e => handleSearchChange('email', e.target.value)}
            sx={{ width: '240px' }}
          />
          <TextField
            select
            size='small'
            value={search.subscriptionTag}
            onChange={e => handleSearchChange('subscriptionTag', e.target.value)}
            sx={{ width: '180px' }}
            displayEmpty
          >
            <MenuItem value=''>All Plans</MenuItem>
            <MenuItem value='TNM_NEWS_MARKET'>News & Market</MenuItem>
            <MenuItem value='TNM_NEWS_DATA'>News & Data</MenuItem>
            <MenuItem value='TNM_DRILLHOLE'>Drill Hole</MenuItem>
            <MenuItem value='TNM_BASIC'>Basic</MenuItem>
          </TextField>
          <TextField
            select
            size='small'
            value={search.renewalFilter}
            onChange={e => handleSearchChange('renewalFilter', e.target.value)}
            sx={{ width: '180px' }}
            displayEmpty
          >
            <MenuItem value=''>All Renewal Dates</MenuItem>
            <MenuItem value='this_month'>This Month</MenuItem>
            <MenuItem value='next_30'>Next 30 Days</MenuItem>
            <MenuItem value='next_60'>Next 60 Days</MenuItem>
            <MenuItem value='next_90'>Next 90 Days</MenuItem>
          </TextField>
          {(search.firmName || search.userName || search.email || search.subscriptionTag || search.renewalFilter) && (
            <Button type='text' onClick={clearSearch}>Clear filters</Button>
          )}
        </Box>

        {error && (
          <Box sx={{ mb: '16px', p: '12px', bgcolor: 'error.light', borderRadius: '6px' }}>
            <Text sx={{ color: 'error.contrastText', fontSize: '14px' }}>{error}</Text>
          </Box>
        )}

        <FirmTable firms={firms} sortBy={sortBy} sortDir={sortDir} onSort={(field) => {
          if (sortBy === field) {
            const nextDir = sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc';
            setSortDir(nextDir);
            setSortBy(nextDir ? field : null);
          } else {
            setSortBy(field);
            setSortDir('asc');
          }
        }} />

        <CreateAccountModal open={createOpen} onClose={() => { setCreateOpen(false); load(pageNo, search, pageSize); }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text sx={{ fontSize: '13px', color: 'text.secondary' }}>Rows per page:</Text>
            <TextField
              select
              size='small'
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
              sx={{ width: '80px' }}
            >
              {PAGE_SIZE_OPTIONS.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            {firms.length > 0 && (
              <Text sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {((pageNo - 1) * pageSize) + 1}–{((pageNo - 1) * pageSize) + firms.length}
              </Text>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              type='outlined'
              disabled={pageNo <= 1}
              onClick={() => handlePage(-1)}
              sx={{ minWidth: '80px' }}
            >
              ← Prev
            </Button>
            <Text sx={{ fontSize: '14px', fontWeight: 600, px: '8px' }}>Page {pageNo}</Text>
            <Button
              type='outlined'
              disabled={firms.length < pageSize}
              onClick={() => handlePage(1)}
              sx={{ minWidth: '80px' }}
            >
              Next →
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
}
